import React, { useEffect } from 'react'
import Typography from '@ui/typography'
import Icon from '@ui/elements/Icon'
import cn from '@ui/utils/cn'
import { AnimatedPressable } from '@ui/animated/nativewindReanimated'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import { InputLabelProps } from './types'
import {
  useAnimatedInputLabelInline,
  useAnimatedInputRequiredIndicator,
} from './useAnimatedInputBorder'
import { labelInlineColor } from './styles'

export default function InputLabelInline({
  label,
  labelWidth = 128,
  onPress,
  icon,
  required,
  success,
  successMessage,
  error,
  errorMessage,
  focus,
  disabled,
  readonly,
}: InputLabelProps) {
  const isVisible = !!icon || !!label

  const visible = useSharedValue(0)

  useEffect(() => {
    visible.set(withSpring(isVisible ? 1 : 0))
  }, [isVisible])

  const errorStyle = useAnimatedStyle(() => {
    const translateX = interpolate(
      errorMessage ? error?.value || 0 : 0,
      [0, 0.1, 0.3, 0.5, 0.6, 1],
      [0, -2, 2, -2, 0, 0]
    )

    return {
      transform: [
        {
          translateX,
        },
      ],
      opacity: visible.value,
    }
  })

  const c = labelInlineColor

  const iconColor = successMessage
    ? c.success
    : errorMessage
    ? c.error
    : disabled?.value
    ? c.disabled
    : readonly?.value
    ? c.readonly
    : c.default

  const styleColor = useAnimatedInputLabelInline({
    focus,
    error,
    success,
    disabled,
    readonly,
  })

  const styleRequiredIndicator = useAnimatedInputRequiredIndicator({
    focus,
    error,
    success,
    disabled,
    readonly,
  })

  return (
    <AnimatedPressable
      onPress={onPress}
      disabled={!isVisible}
      tabIndex={-1}
      className={cn(
        'flex-row items-center border-r border-black/10 px-4',
        isVisible ? 'h-full' : 'pointer-events-none absolute'
      )}
    >
      <Animated.View
        style={errorStyle}
        className="relative inset-0 flex-row items-center gap-2"
      >
        {!!icon && <Icon color={iconColor} name={icon} />}
        {!!label && (
          <Typography
            variant="SMB"
            className="min-w-32 px-2 font-text-semibold"
            style={[styleColor, { minWidth: labelWidth }]}
          >
            {label}
            {required && (
              <Typography
                variant="SMB"
                className="h-1 w-1 rounded-full"
                style={[styleRequiredIndicator, { top: 0, left: 4 }]}
              >
                *
              </Typography>
            )}
          </Typography>
        )}
      </Animated.View>
    </AnimatedPressable>
  )
}
